/*
  <media-gallery> — native scroll-snap product gallery.

  Replaces media-gallery.js (Slick + jQuery main PDP gallery) and lite-gallery.js
  (featured-product, shop-the-look, complementary products, quick shop). The
  track is a plain flex row with `overflow-x: auto; scroll-snap-type: x mandatory`
  (see component-slider.css), so it is usable from first paint; this script only
  adds controls, thumbnail/variant sync and media play/pause.

  Public API kept from media-gallery.js:
    setActiveMedia(mediaId, productOptions, currentVariant)  — variants.js calls this
  Plus:
    goTo(index, instant), refresh()

  Markup contract (unchanged from the Slick version):
    <media-gallery data-section data-block? data-enable-all-images? data-product-swatches data-gallery="lite"?>
      .product-single__wrapper            positioning box for arrows/dots
        ul.product-single__gallery        the scroll track
          li.product-single__gallery-item[data-media-id][data-alt]
      .thumbnails-wrapper ul > li.product-single__thumbnails-item > [data-thumbnail-id]
*/
if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      // One-time DOM setup runs once; the window/document-level hooks that
      // disconnectedCallback removes are (re)attached on every connect, because
      // product-content.js re-parents the section (and so this element) after
      // the gallery has already initialised.
      if (!this.galleryReady) this.setup();
      if (!this.galleryReady) return;

      (this.lite ? this : window).addEventListener('keydown', this.onKeydown);
      if (this.resizeObserver) {
        this.resizeObserver.observe(this.track);
        if (this.thumbTrack) this.resizeObserver.observe(this.thumbTrack);
      }
      if (!this.initialised) {
        this.initialised = true;
        this.initFromVariant();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    setup() {
      this.track = this.querySelector('.product-single__gallery');
      if (!this.track) return;
      this.galleryReady = true;

      this.sectionId = `${this.dataset.section || ''}${this.dataset.block || ''}`;
      this.lite = this.dataset.gallery === 'lite';
      this.wrapper = this.track.closest('.product-single__wrapper') || this.track.parentElement;
      this.slides = Array.from(this.track.children).filter((el) => el.classList.contains('product-single__gallery-item'));
      this.thumbTrack = null;
      const thumbsWrapper = this.querySelector('.thumbnails-wrapper');
      if (thumbsWrapper) this.thumbTrack = thumbsWrapper.querySelector('ul');
      this.thumbItems = this.thumbTrack ? Array.from(this.thumbTrack.children) : [];
      this.lock3DQuery = window.matchMedia('(min-width: 1001px)');
      this.current = null;
      this.pendingTarget = null;

      if (!this.lite) {
        this.buildArrows();
        this.buildDots();
        // Lite galleries used to pre-hide thumbnails past the third one as a
        // pre-Slick CLS guard; the strip now scrolls natively, so show them all.
      } else {
        this.thumbItems.forEach((item) => item.classList.remove('product-single__thumbnails--hide'));
      }

      this.thumbItems.forEach((item) => {
        const button = item.querySelector('[data-thumbnail-id]');
        if (!button) return;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const slide = this.slideByMediaId(button.dataset.thumbnailId);
          if (slide) this.goTo(this.visibleSlides().indexOf(slide));
        });
      });

      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      if ('onscrollend' in window) this.track.addEventListener('scrollend', this.onTrackScroll, { passive: true });

      // Arrow keys: the main PDP gallery listens page-wide (as the Slick version
      // did), lite galleries only while focus is inside them. Attached in
      // connectedCallback.

      // Re-snap to the active slide whenever the track is resized or becomes
      // visible (display:none → block in drawers/modals gives a 0-width track).
      // Targets are observed in connectedCallback.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          window.requestAnimationFrame(() => this.refresh());
        });
      }
    }

    /* ---------- initial state ---------- */

    initFromVariant() {
      const productVariants = document.getElementById(`VariantSelect-${this.sectionId}`);
      let options = null;
      let currentVariant = null;
      let mediaId = null;

      if (productVariants) {
        options = productVariants.productData ? productVariants.productData.options : null;
        currentVariant = productVariants.currentVariant || null;
        // The main gallery only jumps to the variant image when the URL carries
        // ?variant= (so a bare PDP always opens on the first image); lite
        // galleries always start on the current variant's image.
        const variantInUrl = this.lite || new URLSearchParams(window.location.search).get('variant');
        if (currentVariant && currentVariant.featured_media && variantInUrl) {
          mediaId = `${this.sectionId}-${currentVariant.featured_media.id}`;
        }
      }

      this.setActiveMedia(mediaId, options, currentVariant, true);
    }

    /* ---------- helpers ---------- */

    visibleSlides() {
      return this.slides.filter((slide) => !slide.hidden);
    }

    slideByMediaId(mediaId) {
      if (!mediaId) return null;
      return this.slides.find((slide) => slide.dataset.mediaId === String(mediaId)) || null;
    }

    camelize(str) {
      return str.replace(/\W+(.)/g, (match, chr) => chr.toUpperCase());
    }

    colourFromAlt(alt) {
      const match = (alt || '').match(/\(([^)]+)\)/);
      return match ? match[1] : '';
    }

    isLockedSlide(slide) {
      return !!slide && !!slide.querySelector('.product-media-modal__model') && this.lock3DQuery.matches;
    }

    nearestSlide() {
      const visible = this.visibleSlides();
      if (!visible.length) return null;
      const left = this.track.scrollLeft;
      let best = visible[0];
      let bestDistance = Infinity;
      visible.forEach((slide) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = slide;
        }
      });
      return best;
    }

    /* ---------- navigation ---------- */

    goTo(index, instant) {
      const visible = this.visibleSlides();
      if (!visible.length) return;
      const slide = visible[Math.max(0, Math.min(visible.length - 1, index))];
      this.pendingTarget = slide;
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = window.setTimeout(() => { this.pendingTarget = null; }, 1000);
      // Jump instantly onto a 3D-model slide: setActive() switches the track to
      // overflow hidden for it, which would abort an in-flight smooth scroll.
      if (this.isLockedSlide(slide)) instant = true;
      this.track.classList.remove('product-single__gallery--locked');
      this.track.scrollTo({ left: slide.offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      this.setActive(slide);
    }

    prev() {
      const visible = this.visibleSlides();
      this.goTo(Math.max(0, visible.indexOf(this.current) - 1));
    }

    next() {
      const visible = this.visibleSlides();
      this.goTo(Math.min(visible.length - 1, visible.indexOf(this.current) + 1));
    }

    refresh() {
      if (!this.current || this.current.hidden) {
        this.setActive(this.nearestSlide());
        return;
      }
      if (!this.track.clientWidth) return;
      const left = this.current.offsetLeft;
      if (Math.abs(this.track.scrollLeft - left) > 1) {
        this.track.scrollTo({ left: left, behavior: 'auto' });
      }
      this.centreThumbnail(this.current, true);
    }

    onTrackScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        if (!this.track.clientWidth) return;
        const nearest = this.nearestSlide();
        if (!nearest) return;
        if (this.pendingTarget) {
          if (nearest !== this.pendingTarget) return;
          this.pendingTarget = null;
        }
        if (nearest !== this.current) this.setActive(nearest);
      }, 80);
    }

    onKeydown(event) {
      if (event.defaultPrevented) return;
      const target = event.target;
      if (target && (target.closest('input, textarea, select, [contenteditable="true"]'))) return;
      if (event.code === 'ArrowLeft') {
        this.prev();
      } else if (event.code === 'ArrowRight') {
        this.next();
      }
    }

    /* ---------- active state ---------- */

    setActive(slide) {
      if (!slide) return;
      const changed = slide !== this.current;
      this.current = slide;
      const visible = this.visibleSlides();
      const index = visible.indexOf(slide);

      this.slides.forEach((item) => item.classList.toggle('is-active', item === slide));

      this.thumbItems.forEach((item) => {
        const button = item.querySelector('[data-thumbnail-id]');
        const active = !!button && button.dataset.thumbnailId === slide.dataset.mediaId;
        item.classList.toggle('is-active', active);
        if (!button) return;
        button.classList.toggle('active-thumb', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.centreThumbnail(slide, !changed);

      if (this.dots) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }
      if (this.prevButton) {
        this.prevButton.classList.toggle('is-disabled', index <= 0);
        this.nextButton.classList.toggle('is-disabled', index >= visible.length - 1);
      }

      // 3D models take over pointer drags on desktop; stop the track from
      // scrolling underneath them (matches the old Slick swipe:false toggle).
      this.track.classList.toggle('product-single__gallery--locked', this.isLockedSlide(slide));

      if (changed) this.playActiveMedia(slide);
    }

    centreThumbnail(slide, instant) {
      if (!this.thumbTrack || !slide) return;
      const item = this.thumbItems.find((li) => {
        const button = li.querySelector('[data-thumbnail-id]');
        return button && button.dataset.thumbnailId === slide.dataset.mediaId;
      });
      if (!item) return;
      const track = this.thumbTrack;
      const canScrollX = track.scrollWidth > track.clientWidth + 1;
      const canScrollY = track.scrollHeight > track.clientHeight + 1;
      if (!canScrollX && !canScrollY) return;
      const target = {
        behavior: instant ? 'auto' : 'smooth'
      };
      if (canScrollX) target.left = item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2;
      if (canScrollY) target.top = item.offsetTop - (track.clientHeight - item.offsetHeight) / 2;
      track.scrollTo(target);
    }

    playActiveMedia(slide) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia(this);
      if (!slide) return;
      const video = slide.querySelector('video');
      if (video) {
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      }
      const deferredMedia = slide.querySelector('.deferred-media:not(product-model)');
      if (deferredMedia && deferredMedia.loadContent) deferredMedia.loadContent(false);
    }

    /* ---------- controls ---------- */

    buildArrows() {
      if (!this.wrapper || this.slides.length < 2) return;
      const make = (dir, label) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `product-single__gallery-arrow product-single__gallery-arrow--${dir}`;
        button.setAttribute('aria-label', label);
        button.addEventListener('click', () => (dir === 'prev' ? this.prev() : this.next()));
        this.wrapper.appendChild(button);
        return button;
      };
      this.prevButton = make('prev', 'Previous image');
      this.nextButton = make('next', 'Next image');
    }

    buildDots() {
      if (!this.wrapper || this.slides.length < 2) return;
      this.dotsWrap = document.createElement('div');
      this.dotsWrap.className = 'product-single__gallery-dots';
      this.wrapper.appendChild(this.dotsWrap);
      this.renderDots();
    }

    renderDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.dots = this.visibleSlides().map((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(dot);
        return dot;
      });
      this.dotsWrap.hidden = this.dots.length < 2;
    }

    /* ---------- variant → media (called by variants.js) ---------- */

    setActiveMedia(mediaId, productOptions, currentVariant, instant) {
      const enableAllImages = this.dataset.enableAllImages;
      let colorValue = null;

      if (productOptions && currentVariant && this.dataset.productSwatches) {
        const swatches = this.dataset.productSwatches.split(',').map((value) => value.trim());
        productOptions.forEach((option, i) => {
          if (swatches.includes(String(option).toLowerCase().trim())) {
            colorValue = currentVariant.options[i];
          }
        });
      }

      if (colorValue && enableAllImages === 'false') {
        const wanted = this.camelize(colorValue.toLowerCase());
        const matches = (alt) => {
          const colour = this.colourFromAlt(alt);
          return colour === '' || this.camelize(colour.toLowerCase()) === wanted;
        };
        this.slides.forEach((slide) => {
          slide.hidden = !matches(slide.dataset.alt);
        });
        this.thumbItems.forEach((item) => {
          const img = item.querySelector('img');
          const alt = img ? (img.dataset.alt !== undefined ? img.dataset.alt : img.alt) : '';
          item.classList.toggle('product-single__thumbnails--hide', !matches(alt));
        });
      } else {
        this.slides.forEach((slide) => { slide.hidden = false; });
        this.thumbItems.forEach((item) => item.classList.remove('product-single__thumbnails--hide'));
      }
      this.renderDots();

      const visible = this.visibleSlides();
      const wantedSlide = this.slideByMediaId(mediaId);
      let index = wantedSlide && !wantedSlide.hidden ? visible.indexOf(wantedSlide) : 0;
      if (index < 0) index = 0;

      if (!visible.length) return;
      const target = visible[index];
      if (target === this.current && !instant) {
        this.refresh();
        return;
      }
      this.goTo(index, instant || this.current === null);
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
